import { AlertTriangle, Loader2, PlugZap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export type ChannelStatus = 'connected' | 'awaiting-sync' | 'not-connected' | 'error';

interface ChannelStatusNoticeProps {
  status: ChannelStatus;
  /** Human name of the channel, e.g. "Google Search Console". */
  channel: string;
  /** Error text when `status` is 'error'. */
  error?: string | null;
}

/**
 * Explains an unpopulated panel instead of leaving the operator staring at
 * zeros.
 *
 * The Marketing Dashboard reads from eight independent integrations, and at
 * any given time some of them won't be wired up. Distinguishing "nobody has
 * connected this yet" from "connected, first sync still running" from "the
 * sync is failing" is the difference between a dashboard the team trusts and
 * one they stop opening — so each state gets its own copy and its own action.
 *
 * Returns null when the channel is healthy, so callers can render it
 * unconditionally above their content.
 */
export function ChannelStatusNotice({
  status,
  channel,
  error,
}: ChannelStatusNoticeProps) {
  if (status === 'connected') return null;

  if (status === 'awaiting-sync') {
    return (
      <Card className="border-atmosphere-dark/30 bg-atmosphere/10">
        <CardContent className="flex items-start gap-3 p-4">
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-atmosphere-dark" />
          <p className="text-sm font-medium text-foreground">
            {channel} is connected — waiting on the first sync
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card className="border-fresh-salmon-dark/30 bg-fresh-salmon/10">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-fresh-salmon-dark"
              strokeWidth={1.8}
            />
            <div>
              <p className="text-sm font-medium text-foreground">
                Couldn't load {channel} data
              </p>
              <p className="text-xs text-muted-foreground">
                {error ?? 'The last sync failed. Check the integration status and reconnect if needed.'}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/integrations">Open integrations</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-dashed border-border bg-card/50">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-old-growth" strokeWidth={1.8} />
          <p className="text-sm font-medium text-foreground">{channel} isn't connected</p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/integrations">Connect</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * Derives the notice state from a query's flags.
 *
 * `connected` here means "rows exist for the window", which is why an
 * integration can be live on the Integrations page and still resolve to
 * `awaiting-sync` — see the contract note at the top of
 * `src/features/marketing/hooks.ts`.
 */
export function resolveChannelStatus(args: {
  isLoading: boolean;
  error: unknown;
  connected: boolean | undefined;
  /** True when an account/property exists but has produced no metrics yet. */
  awaitingFirstSync?: boolean;
}): ChannelStatus {
  if (args.error) return 'error';
  // Treat the initial load as healthy so the page renders skeletons rather
  // than flashing a "not connected" banner that resolves a moment later.
  if (args.isLoading) return 'connected';
  if (args.connected) return 'connected';
  if (args.awaitingFirstSync) return 'awaiting-sync';
  return 'not-connected';
}
